import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  FileCheck,
  Globe,
  Lock,
  Mail,
  Server,
  Shield,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  DOMAIN_ACCENT,
  DOMAIN_ORDER,
  DOMAIN_TINT,
  LIVE_MODULES,
  MODULES,
} from '../constants/ecosystem';

/**
 * Landing publique de Nexus-360.
 *
 * Applique le systeme de design Nexus v1.0 « Nuit » : surfaces ink,
 * encre creme, accent #8FB6E8, Instrument Serif pour les titres
 * editoriaux et Public Sans pour le reste. Les jetons vivent dans
 * `tailwind.config.js`, jamais en hex dans cette page — a l'exception
 * des accents de domaine, qui sont des donnees (`ecosystem.js`) et non
 * des classes, et doivent donc passer par `style`.
 *
 * Page entierement statique : aucun appel reseau. Le contenu vient de
 * `constants/ecosystem.js` (structure) et de `i18n/*.json` (textes),
 * ce qui la rend identique en FR et en EN et immunise contre une API
 * indisponible.
 */

const TRUST_ICONS = { shield: Shield, 'file-check': FileCheck, server: Server, lock: Lock };

/**
 * Liste traduite, garantie iterable.
 *
 * `t()` renvoie la cle elle-meme quand elle est absente : une chaine, qui
 * passerait un simple `|| []` puis casserait sur `.map`. On verifie donc le
 * type plutot que la seule presence.
 */
function list(value) {
  return Array.isArray(value) ? value : [];
}

const SECTION = 'mx-auto w-full max-w-[1160px] px-6 sm:px-8';
const EYEBROW =
  'text-[11px] font-semibold uppercase tracking-[0.16em] text-cream-faint';
const H2 =
  'font-serif font-normal text-[clamp(1.875rem,4vw,2.625rem)] leading-[1.08] m-0';
/** Bouton primaire : creme sur sombre. L'accent bleu reste aux liens et au focus. */
const BTN_PRIMARY =
  'inline-flex items-center gap-2 rounded-full bg-cream px-7 py-[15px] text-sm font-semibold text-ink-950 transition-colors hover:bg-white';
const LINK_UNDERLINE =
  'text-sm font-semibold text-accent border-b border-accent/45 pb-0.5 transition-colors hover:text-accent-hover';
const CARD = 'rounded-card border border-ink-700 bg-ink-900';
/** ink-950 — seul endroit ou le jeton doit etre repete en JS. */
const PAGE_BG = '#12171F';

/* ---------------------------------------------------------------- NAV */

function Nav({ t, lang, setLang }) {
  const [open, setOpen] = useState(false);
  const links = [
    ['#disponibles', t('landing.nav.live')],
    ['#ecosysteme', t('landing.nav.ecosystem')],
    ['#tarifs', t('landing.nav.pricing')],
    ['#confiance', t('landing.nav.trust')],
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-ink-700 bg-ink-950/[0.88] backdrop-blur-md">
      <div className={`${SECTION} flex h-[68px] items-center justify-between gap-6`}>
        <a href="#top" className="flex shrink-0 items-center gap-3 text-cream">
          <span className="grid h-[34px] w-[34px] place-items-center rounded-icon bg-accent font-serif text-[19px] leading-none text-ink-950">
            N
          </span>
          <span className="text-base font-semibold tracking-[-0.01em]">Nexus-360</span>
        </a>

        <div className="hidden items-center gap-7 text-sm lg:flex">
          {links.map(([href, label]) => (
            <a key={href} href={href} className="text-cream-muted transition-colors hover:text-cream">
              {label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <button
            type="button"
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            aria-label={t('nav.language')}
            className="hidden items-center gap-1.5 p-2 text-[13px] font-semibold text-cream-muted transition-colors hover:text-cream sm:flex"
          >
            <Globe className="h-4 w-4" aria-hidden="true" />
            {lang.toUpperCase()}
          </button>
          <Link
            to="/login"
            className="hidden text-sm font-medium text-cream-muted transition-colors hover:text-cream sm:block"
          >
            {t('nav.login')}
          </Link>
          <Link
            to="/register"
            className="whitespace-nowrap rounded-full bg-cream px-4 py-2.5 text-xs font-semibold text-ink-950 transition-colors hover:bg-white sm:px-5 sm:text-[13.5px]"
          >
            {t('nav.free_trial')}
          </Link>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={t('landing.nav.ecosystem')}
            className="-mr-2 flex h-9 w-8 flex-col items-center justify-center gap-[5px] lg:hidden"
          >
            <span className="block h-px w-5 bg-cream-muted" />
            <span className="block h-px w-5 bg-cream-muted" />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-ink-700 lg:hidden">
          <div className={`${SECTION} flex flex-col gap-1 py-3`}>
            {links.map(([href, label]) => (
              <a
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-2 py-2.5 text-sm text-cream-muted transition-colors hover:bg-ink-800 hover:text-cream"
              >
                {label}
              </a>
            ))}
            <Link
              to="/login"
              className="rounded-lg px-2 py-2.5 text-sm text-cream-muted transition-colors hover:bg-ink-800 hover:text-cream sm:hidden"
            >
              {t('nav.login')}
            </Link>
            <button
              type="button"
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              aria-label={t('nav.language')}
              className="flex items-center gap-2 rounded-lg px-2 py-2.5 text-left text-sm text-cream-muted transition-colors hover:bg-ink-800 hover:text-cream sm:hidden"
            >
              <Globe className="h-4 w-4" aria-hidden="true" />
              {lang === 'fr' ? 'English' : 'Français'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

/* --------------------------------------------------------------- HERO */

function Hero({ t }) {
  return (
    <section id="top" className={`${SECTION} pb-2 pt-16 sm:pt-24`}>
      <p className={`${EYEBROW} mb-6 text-accent`}>{t('landing.hero.eyebrow')}</p>
      <h1 className="m-0 max-w-[860px] font-serif text-[clamp(2.75rem,7vw,5rem)] font-normal leading-[1.0] tracking-[-0.02em] [text-wrap:balance]">
        {t('landing.hero.title_a')}{' '}
        <em className="not-italic text-accent">{t('landing.hero.title_b')}</em>.
      </h1>

      <div className="mt-8 grid items-end gap-10 lg:grid-cols-2">
        <p className="m-0 max-w-[52ch] text-[17px] leading-[1.65] text-cream-muted">
          {t('landing.hero.subtitle')}
        </p>
        <div>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/register" className={BTN_PRIMARY}>
              {t('landing.hero.cta_primary')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a href="#ecosysteme" className={`${LINK_UNDERLINE} whitespace-nowrap`}>
              {t('landing.hero.cta_secondary')} →
            </a>
          </div>
          <p className="mb-0 mt-[18px] text-[13.5px] text-cream-faint">
            {t('landing.hero.foot')}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------ BANDEAU MODULES */

function StripCard({ module: m, label }) {
  const Icon = m.icon;
  return (
    <div
      className={`flex w-[262px] shrink-0 items-center gap-3.5 rounded-card border px-5 py-[18px] transition-colors hover:border-ink-500 ${
        m.live ? 'border-ink-700 bg-ink-900' : 'border-ink-700/75 bg-transparent'
      }`}
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-[11px]"
        style={{ background: DOMAIN_TINT[m.domain], color: DOMAIN_ACCENT[m.domain] }}
      >
        <Icon className="h-[19px] w-[19px]" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${m.live ? 'bg-success' : 'bg-warning'}`}
          />
          <span className="truncate text-[14.5px] font-semibold">{m.name}</span>
        </span>
        <span className="mt-[3px] block truncate text-xs text-cream-faint">{label}</span>
      </span>
    </div>
  );
}

function Strip({ t }) {
  // Deux rangees glissant en sens inverse. Chaque piste est doublee : la
  // translation de -50 % fait boucler la sequence sans saut visible.
  const { rowA, rowB } = useMemo(() => {
    const half = Math.ceil(MODULES.length / 2);
    const first = MODULES.slice(0, half);
    const second = MODULES.slice(half);
    return {
      rowA: [...first, ...second.slice(0, 2)],
      rowB: [...second, ...first.slice(0, 2)],
    };
  }, []);

  const row = (items, animation, keyPrefix) => (
    <div className="overflow-hidden">
      <div className={`flex w-max gap-4 ${animation} hover:[animation-play-state:paused]`}>
        {[...items, ...items].map((m, i) => (
          <StripCard
            key={`${keyPrefix}-${m.slug}-${i}`}
            module={m}
            label={t(`landing.domains.${m.domain}.name`)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <section className="overflow-hidden py-16">
      <div className={`${SECTION} mb-7 flex flex-wrap items-baseline justify-between gap-4`}>
        <p className={EYEBROW}>
          {t('landing.strip.title')} — {t('landing.strip.count')}
        </p>
        <div className="flex flex-wrap gap-[18px] text-[12.5px] text-cream-faint">
          <span className="flex items-center gap-2">
            <span className="h-[7px] w-[7px] rounded-full bg-success" />
            {t('landing.strip.legend_live')}
          </span>
          <span className="flex items-center gap-2">
            <span className="h-[7px] w-[7px] rounded-full bg-warning" />
            {t('landing.strip.legend_soon')}
          </span>
        </div>
      </div>

      <div className="marquee-mask flex flex-col gap-4" aria-hidden="true">
        {row(rowA, 'animate-slide-left', 'a')}
        {row(rowB, 'animate-slide-right', 'b')}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- CHIFFRES */

function Stats({ t }) {
  const items = [
    ['100+', t('landing.stats.structures')],
    ['500+', t('landing.stats.employees')],
    ['1 000+', t('landing.stats.invoices')],
    ['99,9 %', t('landing.stats.uptime')],
  ];
  return (
    <section className="border-y border-ink-700 bg-ink-925">
      <div className={`${SECTION} grid gap-8 py-11 sm:grid-cols-2 lg:grid-cols-4`}>
        {items.map(([value, label]) => (
          <div key={label}>
            <div className="tabular font-serif text-[38px] leading-[1.1]">{value}</div>
            <div className="mt-1 text-[13px] text-cream-faint">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- DISPONIBLES */

function LiveModules({ t }) {
  return (
    <section id="disponibles" className={`${SECTION} scroll-mt-[88px] pt-20 sm:pt-[88px]`}>
      <p className={`${EYEBROW} mb-[18px]`}>{t('landing.live.label')}</p>
      <h2 className={`${H2} max-w-[20ch]`}>{t('landing.live.title')}</h2>
      <p className="mb-0 mt-5 max-w-[58ch] text-base text-cream-muted">
        {t('landing.live.body')}
      </p>

      <div className="mt-10 grid gap-5 lg:grid-cols-2">
        {LIVE_MODULES.map((m) => {
          const Icon = m.icon;
          const accent = DOMAIN_ACCENT[m.domain];
          const features = list(t(`landing.features.${m.slug}`));
          return (
            <div
              key={m.slug}
              className={`${CARD} flex flex-col p-7 transition-colors hover:border-ink-500`}
            >
              <div className="mb-[18px] flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-xl"
                    style={{ background: DOMAIN_TINT[m.domain], color: accent }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <span className="truncate text-lg font-semibold">{m.name}</span>
                </div>
                <span className="shrink-0 rounded-full bg-success/15 px-[11px] py-[5px] text-[11px] font-semibold text-success">
                  {t('landing.live.badge')}
                </span>
              </div>

              <p className="m-0 mb-5 text-[15px] leading-[1.6] text-cream-muted">
                {t(`landing.modules.${m.slug}`)}
              </p>

              <div className="mb-[22px] grid gap-x-5 gap-y-2.5 border-t border-ink-700 pt-5 sm:grid-cols-2">
                {features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5 text-[13.5px] text-cream-muted">
                    <Check
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                      strokeWidth={1.75}
                      style={{ color: accent }}
                      aria-hidden="true"
                    />
                    {f}
                  </div>
                ))}
              </div>

              <a
                href={m.url}
                className="mt-auto text-sm font-semibold transition-opacity hover:opacity-80"
                style={{ color: accent }}
              >
                {t('landing.live.cta', { name: m.name })} →
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ ECOSYSTEME */

function Ecosystem({ t }) {
  return (
    <section id="ecosysteme" className={`${SECTION} scroll-mt-[88px] pt-20 sm:pt-[88px]`}>
      <p className={`${EYEBROW} mb-[18px]`}>{t('landing.eco.label')}</p>
      <h2 className={`${H2} max-w-[22ch]`}>{t('landing.eco.title')}</h2>
      <p className="mb-0 mt-5 max-w-[60ch] text-base text-cream-muted">
        {t('landing.eco.body')}
      </p>

      <div className="mt-14 flex flex-col gap-14">
        {DOMAIN_ORDER.map((key) => {
          const modules = MODULES.filter((m) => m.domain === key);
          return (
            <div key={key}>
              <div className="mb-6 flex flex-wrap items-baseline justify-between gap-4 border-b border-ink-700 pb-3.5">
                <div className="flex items-center gap-3">
                  <span
                    className="h-[9px] w-[9px] rounded-[3px]"
                    style={{ background: DOMAIN_ACCENT[key] }}
                  />
                  <h3 className="m-0 text-[19px] font-semibold">
                    {t(`landing.domains.${key}.name`)}
                  </h3>
                  <span className="text-[13.5px] text-cream-faint">
                    {t(`landing.domains.${key}.tagline`)}
                  </span>
                </div>
                <span className="whitespace-nowrap text-[12.5px] text-cream-faint">
                  {t('landing.eco.count', { count: modules.length })}
                </span>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {modules.map((m) => {
                  const Icon = m.icon;
                  return (
                    <div
                      key={m.slug}
                      className={`flex flex-col gap-2.5 rounded-card border p-5 transition-colors hover:border-ink-500 ${
                        m.live ? 'border-ink-700 bg-ink-900' : 'border-ink-700/90 bg-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2.5">
                        <span
                          className={`grid h-9 w-9 shrink-0 place-items-center rounded-icon ${
                            m.live ? '' : 'bg-ink-900 text-cream-faint'
                          }`}
                          style={
                            m.live
                              ? { background: DOMAIN_TINT[key], color: DOMAIN_ACCENT[key] }
                              : undefined
                          }
                        >
                          <Icon className="h-[17px] w-[17px]" strokeWidth={1.75} aria-hidden="true" />
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-semibold ${
                            m.live
                              ? 'bg-success/15 text-success'
                              : 'bg-warning/[0.12] text-warning'
                          }`}
                        >
                          {m.live ? t('landing.live.badge') : t('landing.eco.badge_soon')}
                        </span>
                      </div>
                      <div className="mt-1 text-[15px] font-semibold">{m.name}</div>
                      <p className="m-0 text-[13.5px] leading-[1.55] text-cream-muted">
                        {t(`landing.modules.${m.slug}`)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ SSO */

function Sso({ t }) {
  const steps = list(t('landing.sso.steps'));
  return (
    <section className={`${SECTION} pt-20 sm:pt-24`}>
      <div className="rounded-3xl border border-ink-700 bg-ink-900 p-7 sm:p-10 lg:p-14">
        <p className={`${EYEBROW} mb-[18px] text-accent`}>{t('landing.sso.label')}</p>
        <h2 className="m-0 max-w-[24ch] font-serif text-[clamp(1.75rem,3.6vw,2.375rem)] font-normal leading-[1.1]">
          {t('landing.sso.title')}
        </h2>
        <div className="mt-11 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => (
            <div key={s.num} className="border-t border-ink-700 pt-5">
              <div className="font-serif text-[22px] text-accent">{s.num}</div>
              <div className="mt-2.5 text-[15px] font-semibold">{s.title}</div>
              <p className="mb-0 mt-2 text-[13.5px] leading-[1.6] text-cream-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- CONFIANCE */

function Trust({ t }) {
  const items = list(t('landing.trust.items'));
  return (
    <section id="confiance" className={`${SECTION} scroll-mt-[88px] pt-20 sm:pt-24`}>
      <p className={`${EYEBROW} mb-[18px]`}>{t('landing.trust.label')}</p>
      <h2 className={`${H2} max-w-[22ch]`}>{t('landing.trust.title')}</h2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => {
          const Icon = TRUST_ICONS[item.icon] || Shield;
          return (
            <div key={item.title} className={`${CARD} p-[22px]`}>
              <span className="grid h-[38px] w-[38px] place-items-center rounded-icon bg-ink-800 text-cream">
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div className="mt-4 text-[15px] font-semibold">{item.title}</div>
              <p className="mb-0 mt-1.5 text-[13.5px] leading-[1.6] text-cream-muted">
                {item.body}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- TARIFS */

function Pricing({ t }) {
  const rules = list(t('landing.pricing.rules'));
  return (
    <section id="tarifs" className={`${SECTION} scroll-mt-[88px] pt-20 sm:pt-24`}>
      <p className={`${EYEBROW} mb-[18px]`}>{t('landing.pricing.label')}</p>
      <h2 className={`${H2} max-w-[20ch]`}>{t('landing.pricing.title')}</h2>
      <p className="mb-0 mt-5 max-w-[58ch] text-base text-cream-muted">
        {t('landing.pricing.body')}
      </p>
      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        {rules.map((r) => (
          <div key={r.title} className={`${CARD} p-6`}>
            <div className="text-[15px] font-semibold">{r.title}</div>
            <p className="mb-0 mt-2 text-[13.5px] leading-[1.6] text-cream-muted">{r.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- CTA */

function FinalCta({ t }) {
  return (
    <section className={`${SECTION} pt-20 sm:pt-24`}>
      <div className="border-t border-ink-700 pt-16 text-center sm:pt-[72px]">
        <h2 className="m-0 mx-auto max-w-[18ch] font-serif text-[clamp(2rem,4.6vw,3.25rem)] font-normal leading-[1.05]">
          {t('landing.cta.title')}
        </h2>
        <p className="mx-auto mb-0 mt-[22px] max-w-[46ch] text-base text-cream-muted">
          {t('landing.cta.body')}
        </p>
        <div className="mt-[34px] flex flex-wrap items-center justify-center gap-5">
          <Link to="/register" className={BTN_PRIMARY}>
            {t('landing.hero.cta_primary')}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link to="/login" className={LINK_UNDERLINE}>
            {t('nav.login')} →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- FOOTER */

function Footer({ t }) {
  const col = 'flex flex-col gap-2.5 text-[13.5px]';
  const link = 'text-cream-muted transition-colors hover:text-cream';
  return (
    <footer className={`${SECTION} pb-14 pt-20 sm:pt-[88px]`}>
      <div className="grid gap-10 border-t border-ink-700 pt-11 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-accent font-serif text-[18px] leading-none text-ink-950">
              N
            </span>
            <span className="text-[15px] font-semibold">Nexus-360</span>
          </div>
          <p className="m-0 max-w-[34ch] text-[13.5px] leading-[1.6] text-cream-faint">
            {t('landing.footer.blurb')}
          </p>
        </div>

        <div>
          <div className="mb-3.5 text-[13px] font-semibold">{t('landing.footer.products')}</div>
          <div className={col}>
            <a href="#disponibles" className={link}>{t('landing.nav.live')}</a>
            <a href="#ecosysteme" className={link}>{t('landing.nav.ecosystem')}</a>
            <a href="#tarifs" className={link}>{t('landing.nav.pricing')}</a>
          </div>
        </div>

        <div>
          <div className="mb-3.5 text-[13px] font-semibold">{t('landing.footer.legal')}</div>
          <div className={col}>
            <Link to="/cgu" className={link}>{t('landing.footer.cgu')}</Link>
            <Link to="/confidentialite" className={link}>{t('landing.footer.privacy')}</Link>
            <Link to="/mentions-legales" className={link}>{t('landing.footer.legal_notices')}</Link>
          </div>
        </div>

        <div>
          <div className="mb-3.5 text-[13px] font-semibold">{t('landing.footer.contact')}</div>
          <div className={col}>
            <a href="mailto:contact@nexus-360.fr" className={`${link} flex items-center gap-2`}>
              <Mail className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
              contact@nexus-360.fr
            </a>
            <span className="text-cream-faint">{t('landing.footer.made_in')}</span>
          </div>
        </div>
      </div>

      <div className="mt-10 border-t border-ink-700 pt-6 text-[12.5px] text-cream-faint">
        {t('landing.footer.copyright')}
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ PAGE */

export default function Landing() {
  const { t, lang, setLang } = useTranslation();

  useEffect(() => {
    const previous = document.documentElement.style.backgroundColor;
    document.documentElement.style.backgroundColor = PAGE_BG;
    return () => {
      document.documentElement.style.backgroundColor = previous;
    };
  }, []);

  return (
    <div className="min-h-screen bg-ink-950 font-sans text-[15px] leading-[1.6] text-cream">
      <Nav t={t} lang={lang} setLang={setLang} />
      <main>
        <Hero t={t} />
        <Strip t={t} />
        <Stats t={t} />
        <LiveModules t={t} />
        <Ecosystem t={t} />
        <Sso t={t} />
        <Trust t={t} />
        <Pricing t={t} />
        <FinalCta t={t} />
      </main>
      <Footer t={t} />
    </div>
  );
}
